/**
 * THE MAP'S CAMERA, OUTSIDE ANY COMPONENT (perf overhaul 2b, step 8c).
 *
 * The camera changes every frame of an orbit, a pan, a glide or a follow shot. Held as the battlefield panel's state,
 * each of those frames re-rendered the whole panel - its forty-odd hooks, its rails, every layer - although only the
 * layers that draw with the camera have anything new to show. So the camera lives here: the drawing layers subscribe
 * (`useCamera`), the gestures read it when they happen (`get`), and everything else never hears of a camera frame.
 *
 * `set` takes a camera or a function of the current one, as React's setter does, and applies it at once; the same
 * camera back is no change and tells nobody. A camera is never mutated (the scene memoises on its identity).
 */
import { useSyncExternalStore } from 'react';

/** A store holding `initial`: get, set, and subscribe (which returns the unsubscribe). */
export function makeCameraStore(initial) {
  let camera = initial;
  const listeners = new Set();
  return {
    get: () => camera,
    set: (next) => {
      const value = typeof next === 'function' ? next(camera) : next;
      if (value === camera) return;
      camera = value;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** The store's camera, re-rendering this component (and only this one) when it changes. */
export const useCamera = (store) => useSyncExternalStore(store.subscribe, store.get, store.get);
